import React, {useEffect, useMemo, useState} from 'react'
import {Group, Image, Layer, Line, Stage} from 'react-konva'
import {DetectionTooltip} from 'src/common/components/detection-tooltip/DetectionTooltip'
import {DetectionTooltipProps} from 'src/common/generics/props/DetectionTooltipProps'
import {ImageDetectionsProps} from 'src/common/generics/props/ImageDetectionsProps'
import {Dim, Pos} from 'src/common/types/generics/ImageDetections'
import useImage from 'src/hooks/useImage'
import {objectTypeList} from 'src/pages/alarms/AlarmsFunctions'
import {boxScale, computeScalar, detectionToBox, dim2pos, pos2dim, posDefault, posScale} from '../../utils/Internal'
import {handlePointsDemarcatedArea} from './KonvaImageDetectionsFunctions'

export const ColorMapDescription: Record<string, string> = {
  '#b81714': 'Detecção de ',
  '#FC5E03': 'Detectado porém abaixo da confiança configurada - ',
  '#0000ff': 'Detectado anteriormente e já despachado',
}

enum ColorsOptionsEnum {
  red = '#b81714',
  orange = '#FC5E03',
  green = '#1a9c03',
  blue = '#0000ff',
}

export function KonvaImageDetections({
  src,
  detections = [],
  hideDetections = false,
  scale = 1,
  width,
  height,
  children,
  StageProps,
  konvaImageRef,
  typeDemarcatedArea,
}: React.PropsWithChildren<ImageDetectionsProps>) {
  const image = useImage(src)
  const [_dimImage, setDimImage] = useState<Dim>({width: 0, height: 0})
  const [tooltipProps, setTooltipProps] = useState<DetectionTooltipProps>({
    x: 0,
    y: 0,
    text: '',
    visible: false,
  })
  const internalScale = computeScalar(scale, _dimImage, {width, height})
  const posImage: Pos = dim2pos(_dimImage)
  const dimScaled: Dim = pos2dim(posDefault(posScale(posImage, internalScale), posImage))

  function updateTooltipProps(props: Partial<DetectionTooltipProps>) {
    setTooltipProps((lastProps) => ({...lastProps, ...props}))
  }

  useEffect(() => {
    image.decode().then(() => {
      setDimImage({width: image.width, height: image.height})
    })
  }, [image])

  const lines = useMemo(() => {
    return detections.map((it, index) => {
      const box = boxScale(detectionToBox(it), internalScale)
      const colorOnDetection =
        it.data && it.data.additionalData && it.data.additionalData.boundBoxColor
          ? it.data.additionalData.boundBoxColor
          : defaultColorOnDetection(it.data?.class ?? '')
      return (
        <React.Fragment key={`konva-line-${index}`}>
          <Line
            key={`detection-line-${index}`}
            points={handlePointsDemarcatedArea(internalScale, it, box, typeDemarcatedArea)}
            strokeWidth={2 * internalScale}
            stroke={colorOnDetection}
            closed
            data={it.data}
          />
        </React.Fragment>
      )
    })
  }, [internalScale, detections, typeDemarcatedArea])

  if (konvaImageRef) {
    if (konvaImageRef.current === null) {
      konvaImageRef.current = {dim: dimScaled, scale: internalScale}
    } else if (konvaImageRef.current.scale !== internalScale) {
      konvaImageRef.current = {dim: dimScaled, scale: internalScale}
    }
  }

  function defaultColorOnDetection(classType: string) {
    const dictionary: {[x: string]: string} = {
      'complete-epi': '#1a9c03',
    }

    return dictionary[classType] ?? '#b81714'
  }

  return (
    <Stage {...StageProps} {...dimScaled}>
      <Layer
        onMouseOver={(event) => {
          const pointer = event?.target?.getStage()?.getPointerPosition()
          const attrs = event?.target?.getAttrs()
          if (!attrs.data) return

          const classOnDetection = attrs.data.class ?? ''
          const colorOnDetection =
            attrs.data && attrs.data.additionalData && attrs.data.additionalData.boundBoxColor
              ? attrs.data.additionalData.boundBoxColor
              : defaultColorOnDetection(classOnDetection)

          const colorDescription = ColorMapDescription[colorOnDetection]
          const accuracyText = `${(100 * attrs.data.accuracy).toFixed(1)}%`
          let text = accuracyText

          if (colorOnDetection === ColorsOptionsEnum.green)
            text = `${accuracyText} - ${objectTypeList.find((obj) => obj.name === classOnDetection)?.label ?? classOnDetection}`
          else if (colorOnDetection === ColorsOptionsEnum.blue) text = `${accuracyText} - \n${colorDescription}`
          else text = `${accuracyText} - \n${colorDescription}\n${objectTypeList.find((obj) => obj.name === classOnDetection)?.label ?? classOnDetection}`

          if (pointer && attrs.data) {
            setTooltipProps({
              text,
              x: pointer.x,
              y: pointer.y,
              visible: true,
            })
          } else {
            updateTooltipProps({visible: false})
          }
        }}
        onMouseOut={() => updateTooltipProps({visible: false})}>
        <Image image={image} {...dimScaled} />
        <Group name='detection-areas'>{!hideDetections && lines}</Group>
      </Layer>
      <DetectionTooltip {...tooltipProps} />
      {dimScaled.height !== 0 && children}
    </Stage>
  )
}
